// Lastning (mockup picking-v1/ship.html, owner 2026-11-02): picked orders stand on the floor until the
// truck takes them. Scanning the pallet (or choosing the order) and pressing "Lastad" records that
// the goods left the building — Trail's own ship step; the ERP is not written. Companies with
// "Fakturerad = utlevererad" on rarely need it, but the picture during the day is right with it.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Api, type SalesOrderDetail, type SalesOrderSummary } from "../api/client.js";
import { fmtQty, t } from "../i18n.js";
import { feedback, unlockAudio } from "../scan/feedback.js";
import { attachWedge } from "../scan/wedge.js";
import { Actions, Banner, Btn, ListItem, Prompt, Rows, ScanBar, Strip, focusScan, type BannerData, type RowData } from "../ui/components.jsx";

const hhmm = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" }) : "");
type Picking = { pallets: { sscc: string; items: string[] }[]; lines: { lineNo: number; lots: string[]; quantity: string; short: string | null }[] };
const short = (sscc: string) => `${sscc.slice(0, 4)}…${sscc.slice(-4)}`;

export function Ship({ onClose }: { onClose: () => void }) {
  const [orders, setOrders] = useState<SalesOrderSummary[] | null>(null);
  const [details, setDetails] = useState<Map<string, SalesOrderDetail>>(new Map());
  const [chosen, setChosen] = useState<SalesOrderDetail | null>(null);
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    Api.salesOrders("picked").then(async (o) => {
      const open = o.sort((a, b) => (a.pickupAt ?? "9").localeCompare(b.pickupAt ?? "9") || (a.pickedAt ?? "").localeCompare(b.pickedAt ?? ""));
      setOrders(open);
      const ds = await Promise.all(open.map((x) => Api.salesOrder(x.id).catch(() => null)));
      setDetails(new Map(ds.filter((d): d is SalesOrderDetail => !!d).map((d) => [d.id, d])));
    }).catch(() => setOrders([]));
  }, []);
  useEffect(load, [load]);

  const byCarrier = useMemo(() => {
    const m = new Map<string, SalesOrderSummary[]>();
    for (const o of orders ?? []) if (o.carrier) m.set(o.carrier, [...(m.get(o.carrier) ?? []), o]);
    return [...m.entries()].filter(([, xs]) => xs.length > 1);
  }, [orders]);

  const open = (o: SalesOrderSummary) => { const d = details.get(o.id); if (d) { feedback("accepted"); setChosen(d); } };
  const onScan = useCallback((raw: string) => {
    const text = raw.replace(/^\]\w\w/, "").trim();
    const sscc = /^\(00\)(\d{18})$/.exec(text)?.[1] ?? /^00(\d{18})$/.exec(text)?.[1] ?? (/^\d{18}$/.test(text) ? text : null);
    const hit = (orders ?? []).find((o) => o.reference === text || (sscc && (details.get(o.id)?.picking as Picking | null)?.pallets.some((p) => p.sscc === sscc)));
    if (hit) open(hit);
    else { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: t("ship.notFound", { code: text.slice(0, 40) }) }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, details]);
  useEffect(() => {
    const el = inputRef.current;
    if (!el || chosen) return;
    focusScan(el);
    return attachWedge(el, { onScan });
  }, [onScan, chosen]);

  const ship = async (ids: string[]) => {
    setBusy(true);
    let ok = 0;
    let blocked: string | null = null;
    for (const id of ids) { try { await Api.shipOrder(id); ok++; } catch (e) { blocked = (e as { body?: { message?: string } } | null)?.body?.message ?? blocked; /* stays in the list */ } }
    setBusy(false);
    feedback(ok === ids.length ? "accepted" : "rejected");
    // Loading waits for the pick to be in the ERP (owner 2026-11-03): the server says why.
    setBanner(ok === ids.length ? { kind: "ok", label: t("ship.done"), message: t("ship.doneMsg", { n: ok }) } : { kind: blocked ? "bad" : "warn", label: blocked ? t("ship.blocked") : t("ship.partly"), message: blocked ?? t("ship.partlyMsg", { ok, n: ids.length }) });
    setChosen(null);
    load();
  };

  if (chosen) {
    const picking = chosen.picking as Picking | null;
    const rows: RowData[] = [];
    for (const p of picking?.pallets ?? []) rows.push({ k: `${t("unit.pallet")} ${short(p.sscc)}`, q: t("rows.kolli", { n: p.items.length }), level: "head" });
    for (const l of chosen.lineItems) { const p = picking?.lines.find((x) => x.lineNo === l.lineNo); if (p) rows.push({ k: `${l.productName ?? l.description ?? l.sku ?? ""}${p.lots.length ? ` · ${p.lots.join(", ")}` : ""}`, q: fmtQty(p.quantity, l.uom), level: "sub" }); }
    return (
      <div className="screen" onClick={unlockAudio}>
        <Strip left={<b>{t("ship.title")}</b>} middle={chosen.reference} online={navigator.onLine} queued={0} stuck={0} />
        <div className="body">
          <Prompt step={`${chosen.reference} · ${chosen.customer?.name ?? ""}`} title={t("ship.confirmTitle")} sub={t("ship.confirmSub", { at: hhmm(chosen.pickedAt), by: (chosen.pickedBy ?? "").replace(/^user:/, ""), pallets: picking?.pallets.length ?? 0 })} />
          <Rows rows={rows} />
          <p className="hint">{t("ship.hint")}</p>
        </div>
        <Actions>
          <Btn kind="quiet" icon="back" grow={false} onClick={() => setChosen(null)} />
          <Btn kind="primary" disabled={busy} onClick={() => void ship([chosen.id])}>{t("ship.do")}</Btn>
        </Actions>
      </div>
    );
  }
  return (
    <div className="screen" onClick={() => { unlockAudio(); focusScan(inputRef.current); }}>
      <Strip left={<b>{t("ship.title")}</b>} middle={orders ? t("ship.waiting", { n: orders.length }) : ""} online={navigator.onLine} queued={0} stuck={0} />
      <div className="body">
        {banner && <Banner {...banner} />}
        <Prompt step={t("ship.title")} title={t("ship.listTitle")} sub={t("ship.listSub")} />
        <ScanBar inputRef={inputRef} placeholder={t("release.scan")} />
        <div className="list">
          {orders?.length === 0 && <p className="hint">{t("ship.none")}</p>}
          {orders?.map((o) => <ListItem key={o.id} title={`${o.reference} · ${o.customer?.name ?? ""}`} tag={o.erpReport?.picked === "failed" ? t("ship.unreported") : o.erpReport?.picked === "pending" || o.erpReport?.picked === "claimed" ? t("ship.reportPending") : undefined} tagKind={o.erpReport?.picked === "failed" ? "bad" : undefined} desc={t("ship.row", { at: hhmm(o.pickedAt), by: (o.pickedBy ?? "").replace(/^user:/, ""), pallets: (details.get(o.id)?.picking as Picking | null)?.pallets.length ?? 0, carrier: o.carrier ?? "—" })} onClick={() => open(o)} />)}
        </div>
      </div>
      <Actions>
        <Btn kind="quiet" icon="back" grow={false} onClick={onClose} />
        {byCarrier.map(([carrier, xs]) => <Btn key={carrier} kind="secondary" disabled={busy} onClick={() => { if (window.confirm(t("ship.allConfirm", { n: xs.length, carrier }))) void ship(xs.map((x) => x.id)); }}>{t("ship.all", { carrier, n: xs.length })}</Btn>)}
      </Actions>
    </div>
  );
}
